import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Phone,
  Users,
  UserX,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  CheckCircle,
  Mail,
  UserCheck
} from 'lucide-react';
import apiService from './services/api';
import { Button, LoadingSpinner, Badge, Panel } from './components';

// Summary card component
function SummaryCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    gray: 'bg-gray-50 text-gray-600',
  };

  return (
    <Panel className="p-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-xl font-semibold">{value}</p>
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color] || colorClasses.gray}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Panel>
  );
}

// Expandable group component for duplicates
function DuplicateGroup({ title, subtitle, persons, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg mb-2">
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
          <div>
            <p className="font-medium text-sm">{title}</p>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
        <Badge variant="neutral">{persons.length} persons</Badge>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-3">
          <div className="space-y-2">
            {persons.map((person, idx) => (
              <div
                key={person.id}
                className="flex items-center justify-between bg-white p-2 rounded border border-gray-100"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">#{person.id}</span>
                  <span className="text-sm font-medium">{person.name || `Person ${person.id}`}</span>
                  <span className="text-xs text-gray-500">{person.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  {person.has_client && (
                    <Badge variant="success">HAS CLIENT</Badge>
                  )}
                  {person.client_id && (
                    <a
                      href={`/clients/${person.client_id}`}
                      className="text-red-800 hover:text-red-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Client without phone row
function ClientWithoutPhoneRow({ client }) {
  return (
    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg mb-2">
      <div className="flex items-center gap-3">
        <UserX className="w-4 h-4 text-amber-500" />
        <div>
          <p className="font-medium text-sm">{client.name}</p>
          <p className="text-xs text-gray-500">
            Client #{client.client_id} · Person #{client.person_id}
            {client.square_id && ` · Square: ${client.square_id}`}
          </p>
        </div>
      </div>
      <a
        href={`/clients/${client.client_id}`}
        className="text-red-800 hover:text-red-600"
      >
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}

export default function DataQualityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('phones');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getDataQualityReport();
      setData(result);
    } catch (err) {
      console.error('Error fetching data quality report:', err);
      setError('Failed to load data quality report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-4">
        <Panel className="p-4 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600">{error}</p>
          <Button onClick={fetchData} className="mt-4">
            Retry
          </Button>
        </Panel>
      </div>
    );
  }

  const summary = data?.summary || {
    duplicate_phones: 0,
    duplicate_names: 0,
    clients_without_phone: 0,
    total_issues: 0,
  };

  const tabs = [
    { id: 'phones', label: 'Dup Phones', count: summary.duplicate_phones },
    { id: 'emails', label: 'Dup Emails', count: summary.duplicate_emails || 0 },
    { id: 'names', label: 'Dup Names', count: summary.duplicate_names },
    { id: 'potential', label: 'Potential Dups', count: summary.potential_duplicates || 0 },
    { id: 'missing', label: 'Missing Phone', count: summary.clients_without_phone },
  ];

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Data Quality</h1>
          <p className="text-sm text-gray-500">Review and fix data issues</p>
        </div>
        <Button
          onClick={fetchData}
          variant="secondary"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <SummaryCard
          icon={AlertTriangle}
          label="Total Issues"
          value={summary.total_issues}
          color={summary.total_issues > 0 ? 'red' : 'gray'}
        />
        <SummaryCard
          icon={Phone}
          label="Duplicate Phones"
          value={summary.duplicate_phones}
          color={summary.duplicate_phones > 0 ? 'amber' : 'gray'}
        />
        <SummaryCard
          icon={Users}
          label="Duplicate Names"
          value={summary.duplicate_names}
          color={summary.duplicate_names > 0 ? 'amber' : 'gray'}
        />
        <SummaryCard
          icon={UserX}
          label="Missing Phone"
          value={summary.clients_without_phone}
          color={summary.clients_without_phone > 0 ? 'blue' : 'gray'}
        />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-red-800 text-red-800'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Panel className="p-4">
        {activeTab === 'phones' && (
          <div>
            {data?.duplicate_phones?.length > 0 ? (
              data.duplicate_phones.map((group, idx) => (
                <DuplicateGroup
                  key={group.normalized_phone}
                  title={formatPhone(group.normalized_phone)}
                  subtitle={`Normalized: ${group.normalized_phone}`}
                  persons={group.persons}
                  defaultExpanded={idx === 0}
                />
              ))
            ) : (
              <EmptyState message="No duplicate phone numbers found" />
            )}
          </div>
        )}

        {activeTab === 'emails' && (
          <div>
            {data?.duplicate_emails?.length > 0 ? (
              data.duplicate_emails.map((group, idx) => (
                <DuplicateGroup
                  key={group.email}
                  title={group.email}
                  subtitle="Same email address"
                  persons={group.persons}
                  defaultExpanded={idx === 0}
                />
              ))
            ) : (
              <EmptyState message="No duplicate emails found" />
            )}
          </div>
        )}

        {activeTab === 'names' && (
          <div>
            {data?.duplicate_names?.length > 0 ? (
              data.duplicate_names.map((group, idx) => (
                <DuplicateGroup
                  key={`${group.name}-${idx}`}
                  title={group.name}
                  persons={group.persons}
                  defaultExpanded={idx === 0}
                />
              ))
            ) : (
              <EmptyState message="No duplicate names found" />
            )}
          </div>
        )}

        {activeTab === 'potential' && (
          <div>
            {data?.potential_duplicates?.length > 0 ? (
              data.potential_duplicates.map((group, idx) => (
                <DuplicateGroup
                  key={`${group.last_name}-${idx}`}
                  title={`Last name: ${group.last_name}`}
                  subtitle="Different first names, both have client records"
                  persons={group.clients.map(c => ({
                    id: c.person_id,
                    name: c.name,
                    phone: c.phone,
                    has_client: true
                  }))}
                  defaultExpanded={idx === 0}
                />
              ))
            ) : (
              <EmptyState message="No potential duplicates found" />
            )}
          </div>
        )}

        {activeTab === 'missing' && (
          <div>
            {data?.clients_without_phone?.length > 0 ? (
              data.clients_without_phone.map((client) => (
                <ClientWithoutPhoneRow key={client.client_id} client={client} />
              ))
            ) : (
              <EmptyState message="All clients have phone numbers" />
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}

// Helper function to format phone number for display
function formatPhone(phone) {
  if (!phone || phone.length !== 10) return phone;
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
}

// Empty state component
function EmptyState({ message }) {
  return (
    <div className="text-center py-8">
      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
        <CheckCircle className="w-6 h-6 text-green-600" />
      </div>
      <p className="text-gray-500">{message}</p>
    </div>
  );
}
